import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

// Actions
import { getCampaignMedia } from "../../../actions/campaign-media/CampaignMediaActions";

// Constants and utils
import { defaultPagination } from "../../../constants/UrlConstants";
import { CampaignState } from "./planningConstants";
import { formatText } from "../../../common-utils/string-utils/StringUtils";
import { getCitySpecificMediaList } from "../media-selection/MediaContainerUtil";
import { getDigitalMediasSpotCount } from "../../utils/campaignPlanningUtil";
import { constructRedirectPath } from "../../../utils/redirect-utils/RedirectUtils";

//  Components
import TableHeaders from "../../../components/table/TableHeaders";
import AlertMessage from "../../../mavin/components/alert-message/AlertMessage";
import NoTableDataMessage from "../../../components/table/NoTableDataMessage";
import {
  MediaInfo,
  TgSpecificOts,
  GenericOts,
  MediaDurationCell,
} from "./MediaRow";

function getMediaImpTableHeaders(isLiveOrCompleteCampaign = false) {
  // live headers for impressions
  if (isLiveOrCompleteCampaign) {
    return [
      {
        title: {
          displayName: "Live Target Imp",
          className: "col-1 align-middle text-right",
        },
        subTitle: {
          displayName: "Est Target Imp",
          className: "sub-text",
        },
      },
      {
        title: {
          displayName: "Live General Imp",
          className: "col-1 align-middle text-right",
        },
        subTitle: {
          displayName: "Est General Imp",
          className: "sub-text",
        },
      },
    ];
  }

  // only est impressions
  return [
    {
      title: {
        displayName: "Est Target Imp",
        className: "col-1 align-middle text-right",
      },
    },
    {
      title: {
        displayName: "Est General Imp",
        className: "col-1 align-middle text-right",
      },
    },
  ];
}

function getMediaSpotsTableHeaders(isDigital, digitalMediaSpotCounts) {
  if (!isDigital) {
    return [];
  }

  // adding Spot column for DOOH Campaign
  return [
    {
      title: {
        displayName: "Spots",
        className: "col-1 align-middle text-center",
      },
      subTitle: {
        displayName: `Total (${digitalMediaSpotCounts})`,
        className: "sub-text",
      },
    },
  ];
}

function getMediaTableHeaders(
  isLiveOrCompleteCampaign,
  isDigital = false,
  digitalMediaSpotCounts
) {
  const impressionTableHeader = getMediaImpTableHeaders(
    isLiveOrCompleteCampaign
  );
  const spotsTableHeader = getMediaSpotsTableHeaders(
    isDigital,
    digitalMediaSpotCounts
  );

  // table headers
  const tableHeaders = [
    {
      title: {
        displayName: "Site Name",
        className: "col-3 align-middle",
      },
    },
    {
      element: (
        <th className="col-2 align-middle">
          <p className="mb-0">Zone</p>
          <p className="mb-0">Area</p>
          <p className="mb-0">Route</p>
        </th>
      ),
    },
    {
      title: {
        displayName: "Road Stretch",
        className: "col-2 align-middle",
      },
    },
    {
      title: {
        displayName: "Start and End Date",
        className: "col-2 align-middle text-right",
      },
      subTitle: {
        displayName: "Duration",
        className: "sub-text",
      },
    },
    ...impressionTableHeader,
    ...spotsTableHeader,
  ];

  return tableHeaders;
}

export function RoadStretchInfo({ stretchInfo = {} }) {
  const { name } = stretchInfo || {};
  return (
    <td className="align-top max-w-250">
      <p className="mb-0 text-truncate-3" title={name}>
        {name}
      </p>
    </td>
  );
}

function ZoneRouteAndAreaInfo({ media, stretchInfo }) {
  const { zoneName, region } = media || {};
  const { metaData } = stretchInfo || {};
  const { routeType } = metaData || {};

  return (
    <td className="align-top max-w-250">
      <p className="mb-0 text-truncate" title={zoneName}>
        {formatText(zoneName)}
      </p>
      <p className="mb-0 text-truncate" title={region}>
        {formatText(region)}
      </p>
      <p className="mb-0 text-truncate" title={routeType}>
        {formatText(routeType)}
      </p>
    </td>
  );
}

function DigitalMediaSpots({ media = {} }) {
  const { noOfSpotsPerSlot } = media || {};

  return (
    <td className="text-center align-top">{formatText(noOfSpotsPerSlot)}</td>
  );
}

function MediaDetailsRow({
  media = {},
  mediaPerformance,
  stretchInfo = {},
  roadStretchOtsMap = {},
  isDigital = false,
  campaignPlan = {},
  isLiveOrCompleteCampaign = false,
}) {
  const { roadStretchId } = media || {};

  // stretch ots details
  const stretchFromCityPlan = roadStretchOtsMap[roadStretchId];
  const { ots, otsLit, targetOts, targetOtsLit } = stretchFromCityPlan || {};

  // ots values
  const estGeneric = otsLit ?? ots;
  const estTgSpecific = targetOtsLit ?? targetOts;

  // Calculation for Live Impressions
  const { targetOtsServedTotal, otsServedTotal } = mediaPerformance || {};

  return (
    <tr>
      <MediaInfo media={media} />
      <ZoneRouteAndAreaInfo media={media} stretchInfo={stretchInfo} />
      <RoadStretchInfo stretchInfo={stretchInfo} />
      <MediaDurationCell campaignPlan={campaignPlan} />
      <TgSpecificOts
        estTgSpecific={estTgSpecific}
        liveTgSpecific={targetOtsServedTotal}
        isShowLiveMetrics={isLiveOrCompleteCampaign}
      />
      <GenericOts
        estGeneric={estGeneric}
        liveGeneric={otsServedTotal}
        isShowLiveMetrics={isLiveOrCompleteCampaign}
      />

      {/* for digital campaign */}
      {isDigital && <DigitalMediaSpots media={media} />}
    </tr>
  );
}

function TableActionsSection({ citySpecificMediaList = [], planStatus = "" }) {
  // getting campaignId and cityId from campaignMedia
  const firstCampaignCityMedia = citySpecificMediaList?.[0] || {};
  const { campaignId, cityId } = firstCampaignCityMedia || {};

  // checking campaign is scheduled or live
  const {
    LIVE,
    SCHEDULED,
    //  REVIEW
  } = CampaignState;
  // const isReviewCampaign = planStatus === REVIEW;
  const isScheduledOrLiveCampaign = [LIVE, SCHEDULED].includes(planStatus);

  // page Url
  const creativeUploadPageUrl = `/campaign/${campaignId}/city/${cityId}/creative-upload`;

  // same monitoring page from mavin side
  const manageMonitoringPageUrl = `/monitor/settings/campaign/${campaignId}/city/${cityId}`;

  // link buttons data
  const buttonLinksData = [
    {
      isShow: false, // change when needed { isShow: isReviewCampaign }
      pageUrl: creativeUploadPageUrl,
      label: "Creative Upload",
    },
    {
      isShow: isScheduledOrLiveCampaign,
      pageUrl: manageMonitoringPageUrl,
      label: "Manage Monitoring",
      className: "ml-4",
    },
  ];

  return (
    <div className="d-flex align-items-center justify-content-between mb-2">
      <h4>{"Media Sites"}</h4>

      <div className="d-fle align-items-center">
        {buttonLinksData.map((data, i) => {
          const { isShow, pageUrl, label, className = "" } = data;

          if (!isShow) {
            return null;
          }

          return (
            <Link
              key={i}
              to={constructRedirectPath(pageUrl)}
              target="_blank"
              className={`btn btn-primary shadow-none ${className}`}
            >
              {label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Media Table
 */
function MediaDetailsTable({
  campaignId,
  campaign,
  campaignPlan,
  campaignMediaOtsLogSummary = {},
  roadStretchMap = {},
}) {
  const dispatch = useDispatch();

  // Selector
  const campaignMedias = useSelector(
    (state) => state.campaignMedia.campaignMedia
  );

  // Loading
  const campaignMediasLoading = useSelector(
    (state) => state.campaignMedia.campaignMediaLoading
  );

  const {
    roadStretchOtsMap = {},
    cityId,
    isDigital = false,
  } = campaignPlan || {};
  const { planStatus } = campaign || {};

  // city specific medias
  const citySpecificMediaList = getCitySpecificMediaList(
    campaignMedias,
    cityId
  );
  const isCityMediasPresent = citySpecificMediaList.length > 0;

  const { pageNo, pageSize } = defaultPagination;
  useEffect(() => {
    dispatch(getCampaignMedia(campaignId, pageNo, pageSize));
  }, [dispatch, campaignId, cityId]);

  // Loading
  if (campaignMediasLoading) {
    return (
      <AlertMessage
        className="justify-content-center p-3 my-4"
        textMessage="Media Details Table Loading, Please wait.."
      />
    );
  }

  // Checks for data
  if (!campaignPlan || Object.keys(roadStretchMap).length < 1) {
    return null;
  }

  // checking for campaign state
  const isLiveCampaign = planStatus === CampaignState.LIVE;
  const isCompleteCampaign = planStatus === CampaignState.COMPLETED;
  const isLiveOrCompleteCampaign = isLiveCampaign || isCompleteCampaign;

  // total spot count of campaignMedia
  const digitalMediaSpotCounts = getDigitalMediasSpotCount(
    citySpecificMediaList
  );

  const tableHeaders = getMediaTableHeaders(
    isLiveOrCompleteCampaign,
    isDigital,
    digitalMediaSpotCounts
  );

  return (
    <div className="table-responsive mt-5">
      <TableActionsSection
        citySpecificMediaList={citySpecificMediaList}
        planStatus={planStatus}
      />

      <table className="table table-media">
        <TableHeaders tableHeaders={tableHeaders} headerClass={"thead"} />
        <tbody>
          {/* no table data */}
          {!isCityMediasPresent && (
            <NoTableDataMessage
              colSpan={6}
              message="City don't have any media sites"
            />
          )}

          {/* city medias */}
          {isCityMediasPresent &&
            citySpecificMediaList.map((eachMedia) => (
              <MediaDetailsRow
                key={eachMedia.id}
                media={eachMedia}
                mediaPerformance={
                  campaignMediaOtsLogSummary[eachMedia.id] || {}
                }
                stretchInfo={roadStretchMap[eachMedia.roadStretchId]}
                roadStretchOtsMap={roadStretchOtsMap}
                isDigital={isDigital}
                campaignPlan={campaignPlan}
                isLiveOrCompleteCampaign={isLiveOrCompleteCampaign}
              />
            ))}
        </tbody>
      </table>
    </div>
  );
}

export default MediaDetailsTable;
